import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { emailValidator } from 'app/common/email-validator.directive';
import { nameValidator } from 'app/common/name-validator.directive';
import { numberValidator } from 'app/common/number-validator.directive';
import { phoneValidator } from 'app/common/phone-validator.directive';
import { AuthService } from 'app/core/auth/auth.service';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { Country } from 'app/modules/contacts/contacts.types';
import { BusinessAccountService } from 'app/shared/apis/business-account/api/businessAccount.service';
import { BusinessAccountCreateCommand } from 'app/shared/apis/business-account/model/businessAccountCreateCommand';
import { BusinessAccountDto } from 'app/shared/apis/business-account/model/businessAccountDto';
import { BusinessAccountUpdateCommand } from 'app/shared/apis/business-account/model/businessAccountUpdateCommand';
import { MobileNumberDisplayPipe } from 'app/shared/pipes/mobile-number-display.pipe';
import { Guid } from 'guid-typescript';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'business-account-add',
    templateUrl: './business-account-add.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class BusinessAccountAddComponent implements OnInit, OnDestroy {
    @Input() businessAccountForm: UntypedFormGroup;
    @Input() routeParam: string;
    @Input() businessAccount$: BusinessAccountDto;
    @Input() displayAvatar: boolean;
    @Input() allowMultipleEntries: boolean;
    @Input() navigateAfterSave: boolean;
    @Input() displayInDialog: boolean;
    @Output() editModeToggle = new EventEmitter<boolean>();
    @ViewChild('avatarFileInput') private _avatarFileInput: ElementRef;

    countries: Country[];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formBuilder: UntypedFormBuilder,
        // private _contactService: ContactService,
        private _businessAccountService: BusinessAccountService,
        private _contactsService: ContactsService,
        private _authService: AuthService,
        private route: ActivatedRoute,
        private _mobileNumberDisplayPipe: MobileNumberDisplayPipe
    ) {
      this._contactsService.getCountries().subscribe();
    }

    ngOnInit(): void {
        // Get the country telephone codes
        this._contactsService.countries$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((codes: Country[]) => {
                this.countries = codes;
            });

        this.initializeBusinessAccountForm();

        if (this.routeParam === 'newBusinessAccount') {
            this.businessAccount$ = {};
            this.businessAccountForm.reset();
            this.businessAccountForm.get('isActive').setValue(true);
            this.prepareBusinessAccountFormsForAdd();
        } else {
            // Patch values to the form
            this.businessAccountForm.patchValue(this.businessAccount$);
            this.prepareBusinessAccountFormsForEdit();
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.businessAccount$ = {};
    }

    toggleEditMode(isEditMode: boolean): void {
        this.editModeToggle.emit(isEditMode);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * Get country info by iso code
     *
     * @param iso
     */
    getCountryByIso(iso: string): Country {
        return this.countries?.find(country => country.iso === iso);
    }

    /**
     * Get country info by name
     *
     * @param iso
     */
    getCountryByName(name: string): Country {
        return this.countries?.find(country => country.name === name);
    }

    initializeBusinessAccountForm(): void {
        try {
            // Create the business account form
            this.businessAccountForm = this._formBuilder.group({
                id: [''],
                name: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(1),
                        Validators.maxLength(50),
                    ],
                ],
                emails: this._formBuilder.array([]),
                phoneNumbers: this._formBuilder.array([]),
                mobileNumbers: this._formBuilder.array([]),
                addresses: this._formBuilder.array([]),
                websites: this._formBuilder.array([]),
                isActive: true,
            });
        } catch (ex) {
            console.error('Exception in initializeBusinessAccountForm()');
            console.error(ex);
        }
    }

    prepareBusinessAccountFormsForAdd(): void {
        try {
            // Clear the emails and phoneNumbers form arrays
            (this.businessAccountForm.get('emails') as UntypedFormArray).clear();
            (this.businessAccountForm.get('phoneNumbers') as UntypedFormArray).clear();
            (this.businessAccountForm.get('mobileNumbers') as UntypedFormArray).clear();
            (this.businessAccountForm.get('addresses') as UntypedFormArray).clear();
            (this.businessAccountForm.get('websites') as UntypedFormArray).clear();

            // Add the email form groups to the emails form array
            (this.businessAccountForm.get('emails') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.required,
                            Validators.minLength(1),
                            Validators.maxLength(250),
                            emailValidator(),
                        ],
                    ],
                    label: ['Email'],
                })
            );

            // Add the website form groups to the websites form array
            (this.businessAccountForm.get('websites') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [''],
                    label: ['Web Site'],
                })
            );

            // Add the email form groups to the emails form array
            (this.businessAccountForm.get('addresses') as UntypedFormArray).push(
                this._formBuilder.group({
                    street: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // area: [
                    //     '',
                    //     [
                    //         Validators.required,
                    //         Validators.minLength(1),
                    //         Validators.maxLength(250),
                    //     ],
                    // ],
                    city: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    pincode: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(6)],
                    ],
                    landmark: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // district: ['', [Validators.required, nameValidator()]],
                    // state: ['', [Validators.required, nameValidator()]],
                    country: ['', [Validators.required, nameValidator()]],
                })
            );

            // Add the phone numbers form groups to the phone numbers form array
            (this.businessAccountForm.get('phoneNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Phone'],
                    country: ['us'],
                })
            );

            // Add the phone numbers form groups to the phone numbers form array
            (this.businessAccountForm.get('mobileNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Mobile'],
                    country: ['us'],
                })
            );
        } catch (ex) {}
    }

    prepareBusinessAccountFormsForEdit(): void {
        // Clear the emails and phoneNumbers form arrays
        (this.businessAccountForm.get('emails') as UntypedFormArray).clear();
        (this.businessAccountForm.get('phoneNumbers') as UntypedFormArray).clear();
        (this.businessAccountForm.get('mobileNumbers') as UntypedFormArray).clear();
        (this.businessAccountForm.get('addresses') as UntypedFormArray).clear();
        (this.businessAccountForm.get('websites') as UntypedFormArray).clear();

        // Setup the emails form array
        if (this.businessAccount$.emails && this.businessAccount$.emails.length > 0) {
            // Iterate through them
            this.businessAccount$.emails.forEach((email) => {
                // Add the email form groups to the emails form array
                (this.businessAccountForm.get('emails') as UntypedFormArray).push(
                    this._formBuilder.group({
                        value: [
                            email.value,
                            [
                                Validators.required,
                                Validators.minLength(1),
                                Validators.maxLength(250),
                                emailValidator(),
                            ],
                        ],
                        label: [email.label],
                    })
                );
            });
        } else {
            // Create an email form group
            (this.businessAccountForm.get('emails') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.required,
                            Validators.minLength(1),
                            Validators.maxLength(250),
                            emailValidator(),
                        ],
                    ],
                    label: ['Email'],
                })
            );
        }

        // Setup the websites form array
        if (this.businessAccount$.websites && this.businessAccount$.websites.length > 0) {
            // Iterate through them
            this.businessAccount$.websites.forEach((website) => {
                // Add the website form groups to the websites form array
                (this.businessAccountForm.get('websites') as UntypedFormArray).push(
                    this._formBuilder.group({
                        value: [website.value],
                        label: [website.label],
                    })
                );
            });
        } else {
            // Create website form group
            (this.businessAccountForm.get('websites') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [''],
                    label: ['Web Site'],
                })
            );
        }

        // Setup the addresses form array
        if (this.businessAccount$.addresses && this.businessAccount$.addresses.length > 0) {
            // Iterate through them
            this.businessAccount$.addresses.forEach((address) => {
                // Add the email form groups to the emails form array
                (this.businessAccountForm.get('addresses') as UntypedFormArray).push(
                    this._formBuilder.group({
                        street: [address.street],
                        area: [address.area],
                        city: [address.city],
                        // pincode: [address.pincode],
                        landmark: [address.landmark],
                        // district: [address.district],
                        // state: [address.state],
                        country: [this.getCountryByName(address.country).iso, [Validators.required]],
                    })
                );
            });
        } else {
            // Create an email form group
            (this.businessAccountForm.get('addresses') as UntypedFormArray).push(
                this._formBuilder.group({
                    street: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // area: [
                    //     '',
                    //     [
                    //         Validators.required,
                    //         Validators.minLength(1),
                    //         Validators.maxLength(250),
                    //     ],
                    // ],
                    city: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    pincode: [
                        '',
                        [
                            Validators.minLength(1),
                            Validators.maxLength(6),
                            numberValidator(),
                        ],
                    ],
                    landmark: [
                        '',
                        [Validators.minLength(1), Validators.maxLength(250)],
                    ],
                    // district: ['', [Validators.required, nameValidator()]],
                    // state: ['', [Validators.required, nameValidator()]],
                    country: ['', [Validators.required, nameValidator()]],
                })
            );
        }

        // Setup the phone numbers form array
        if (
            this.businessAccount$.phoneNumbers &&
            this.businessAccount$.phoneNumbers.length > 0
        ) {
            // Iterate through them
            this.businessAccount$.phoneNumbers.forEach((phoneNumber) => {
                // Add the phone numbers form groups to the phone numbers form array
                (this.businessAccountForm.get('phoneNumbers') as UntypedFormArray).push(
                    this._formBuilder.group({
                        value: [
                            this._mobileNumberDisplayPipe.transform(phoneNumber.value,
                                this.getCountryByIso(phoneNumber.country).code),
                            [
                                Validators.minLength(1),
                                phoneValidator(),
                            ],
                        ],
                        label: [phoneNumber.label],
                        country: [phoneNumber.country],
                    })
                );
            });
        } else {
            // Create a phone number form group
            (this.businessAccountForm.get('phoneNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Phone'],
                    country: ['us'],
                })
            );
        }

        // Setup the phone numbers form array
        if (
            this.businessAccount$.mobileNumbers &&
            this.businessAccount$.mobileNumbers.length > 0
        ) {
            // Iterate through them
            this.businessAccount$.mobileNumbers.forEach((mobileNumber) => {
                // Add the phone numbers form groups to the phone numbers form array
                (
                    this.businessAccountForm.get('mobileNumbers') as UntypedFormArray
                ).push(
                    this._formBuilder.group({
                        value: [
                            this._mobileNumberDisplayPipe.transform(mobileNumber.value,
                                this.getCountryByIso(mobileNumber.country).code),
                            [
                                Validators.minLength(1),
                                Validators.maxLength(10),
                                phoneValidator(),
                            ],
                        ],
                        label: [mobileNumber.label],
                        country: [mobileNumber.country],
                    })
                );
            });
        } else {
            // Create a phone number form group
            (this.businessAccountForm.get('mobileNumbers') as UntypedFormArray).push(
                this._formBuilder.group({
                    value: [
                        '',
                        [
                            Validators.minLength(1),
                            phoneValidator(),
                        ],
                    ],
                    label: ['Mobile'],
                    country: ['us'],
                })
            );
        }
    }

    /**
     * Add the email field
     */
    addEmailField(): void {
        // Create an empty email form group
        const emailFormGroup = this._formBuilder.group({
            value: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.maxLength(250),
                    emailValidator(),
                ],
            ],
            label: ['Email'],
        });

        // Add the email form group to the emails form array
        (this.businessAccountForm.get('emails') as UntypedFormArray).push(
            emailFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Remove the email field
     *
     * @param index
     */
    removeEmailField(index: number): void {
        // Get form array for emails
        const emailsFormArray = this.businessAccountForm.get(
            'emails'
        ) as UntypedFormArray;

        // Remove the email field
        emailsFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Add an empty phone number field
     */
    addPhoneNumberField(): void {
        // Create an empty phone number form group
        const phoneNumberFormGroup = this._formBuilder.group({
            value: [
                '',
                [
                    Validators.minLength(1),
                    phoneValidator(),
                ],
            ],
            label: ['Phone'],
            country: ['us'],
        });

        // Add the phone number form group to the phoneNumbers form array
        (this.businessAccountForm.get('phoneNumbers') as UntypedFormArray).push(
            phoneNumberFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Update the business account
     */
    updateBusinessAccount(): void {
        if (this.businessAccountForm.invalid) {
            return;
        }
        // Get the business account object
        const businessAccount = this.businessAccountForm.getRawValue() as BusinessAccountDto;

        businessAccount.code = businessAccount.name;
        // Go through the business account object and clear empty values
        businessAccount.emails = businessAccount.emails.filter(email => email.value);

        businessAccount.phoneNumbers = businessAccount.phoneNumbers.filter(
            phoneNumber => phoneNumber.value
        );

        businessAccount.mobileNumbers = businessAccount.mobileNumbers.filter(
            mobileNumber => mobileNumber.value
        );

        businessAccount.addresses = businessAccount.addresses.filter(
            address => address.country
        );

        businessAccount.addresses.forEach((address) => {
            address.country = this.getCountryByIso(address.country).name;
        });

        businessAccount.websites = businessAccount.websites.filter(website => website.value);

        if (this.routeParam === 'newBusinessAccount') {
            businessAccount.id = Guid.create().toString();

            const businessAccountCreateCommand = new BusinessAccountCreateCommand();
            businessAccountCreateCommand.businessAccountDetails = businessAccount;
            this._businessAccountService
                .businessAccountPost(
                    this._authService.selectedTenant.id,
                    businessAccountCreateCommand,
                    Guid.create().toString()
                )
                .subscribe((data) => {
                    this.toggleEditMode(false);
                    // this.location.back();
                    if (this.navigateAfterSave) {
                      this._router.navigate(['../'], { relativeTo: this.route });
                    }
                });
        } else {
            // Update the business account on the server
            businessAccount.tenantId = this._authService.selectedTenant.id;

            const businessAccountUpdateCommand = new BusinessAccountUpdateCommand();
            businessAccountUpdateCommand.businessAccountDetails = businessAccount;

            this._businessAccountService
                .businessAccountPut(this._authService.selectedTenant.id, businessAccountUpdateCommand)
                .subscribe((data) => {
                    this.toggleEditMode(false);
                    // this.location.back();
                    if (this.navigateAfterSave) {
                      this._router.navigate(['../'], { relativeTo: this.route });
                    }
                });
        }
    }

    /**
     * Add the websites field
     */
    addWebsiteField(): void {
        // Create an empty websites form group
        const websitesFormGroup = this._formBuilder.group({
            value: [''],
            label: ['Web Site'],
        });

        // Add the website form group to the websites form array
        (this.businessAccountForm.get('websites') as UntypedFormArray).push(
            websitesFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Remove the websites field
     *
     * @param index
     */
    removeWebsitesField(index: number): void {
        // Get form array for websites
        const websitesFormArray = this.businessAccountForm.get(
            'websites'
        ) as UntypedFormArray;

        // Remove the email field
        websitesFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Add an empty Mobile number field
     */
    addMobileNumberField(): void {
        // Create an empty mobile number form group
        const mobileNumberFormGroup = this._formBuilder.group({
            value: [
                '',
                [
                    Validators.minLength(1),
                    phoneValidator(),
                ],
            ],
            label: ['Mobile'],
            country: ['us'],
        });

        // Add the phone number form group to the phoneNumbers form array
        (this.businessAccountForm.get('mobileNumbers') as UntypedFormArray).push(
            mobileNumberFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }
    /**
     * Remove the phone number field
     *
     * @param index
     */
    removePhoneNumberField(index: number): void {
        // Get form array for phone numbers
        const phoneNumbersFormArray = this.businessAccountForm.get(
            'phoneNumbers'
        ) as UntypedFormArray;

        // Remove the phone number field
        phoneNumbersFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Add an empty address field
     */
    addAddressesField(): void {
        // Create an empty address form group
        const addressesFormGroup = this._formBuilder.group({
            street: [''],
            // area: [''],
            city: [''],
            pincode: [''],
            landmark: [''],
            // district: [''],
            // state: [''],
            country: ['', [Validators.required]],
        });

        // Add the address form group to the addresses form array
        (this.businessAccountForm.get('addresses') as UntypedFormArray).push(
            addressesFormGroup
        );

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Remove the mobile number field
     *
     * @param index
     */
    removeMobileNumberField(index: number): void {
        // Get form array for Mobile numbers
        const mobileNumbersFormArray = this.businessAccountForm.get(
            'mobileNumbers'
        ) as UntypedFormArray;

        // Remove the mobile number field
        mobileNumbersFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    removeAddressesField(index: number): void {
        // Get form array for Mobile numbers
        const addressesNumbersFormArray = this.businessAccountForm.get(
            'addresses'
        ) as UntypedFormArray;

        // Remove the mobile number field
        addressesNumbersFormArray.removeAt(index);

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }
}
